import React from "react";

import { mdiFilmstrip, mdiMicrophone } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { getElementByUuid } from "../../../utils/data/Content";

import type { Stream } from "../../../api/EncoderAPI.types";
import type { AppState } from "../../../reducers";

interface DetectedTracksProps {
  streamList: Stream[];
}

export default function DetectedTracks({
  streamList,
}: DetectedTracksProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");
  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );

  const videoStreamList = streamList.filter(
    (stream) => stream.codec_type === "video",
  );
  const audioStreamList = streamList.filter(
    (stream) => stream.codec_type === "audio",
  );

  function resolveLanguageLabel(languageUuid: string): string {
    const lang = getElementByUuid(languageUuid, languages);
    if (!lang) return languageUuid.toUpperCase();
    return t(`languages:langs.${lang.language}`);
  }

  return (
    <div className="px-4 pb-3">
      <p className="text-2xs text-muted-foreground mb-3 font-medium tracking-wider uppercase">
        {t("detectedTracks")}
      </p>
      <div className="flex flex-col gap-2">
        {videoStreamList.map((stream) => (
          <div
            key={stream.index}
            className="border-border bg-card flex items-center gap-2.5 rounded-md border px-2.5 py-1.75"
          >
            <Icon
              className="text-primary shrink-0"
              path={mdiFilmstrip}
              size={0.625}
            />
            <span className="text-foreground flex-1 text-[0.78125rem] font-medium">
              {t("videoStream", { codec: stream.codec_name.toUpperCase() })}
            </span>
            {stream?.bit_rate ? (
              <span className="text-muted-foreground text-xs">{`${Math.round(Number(stream.bit_rate) / 1000)} kbps`}</span>
            ) : null}
          </div>
        ))}
        {audioStreamList.map((stream) => (
          <div
            key={stream.index}
            className="border-border bg-card flex items-center gap-2.5 rounded-md border px-2.5 py-1.75"
          >
            <Icon
              className="text-primary shrink-0"
              path={mdiMicrophone}
              size={0.625}
            />
            <span className="text-foreground flex-1 text-[0.78125rem] font-medium">
              {t("audioStream", { codec: stream.codec_name.toUpperCase() })}
            </span>
            {stream.language_uuid ? (
              <span className="border-border text-muted-foreground rounded border px-1.5 py-0.5 text-xs">
                {resolveLanguageLabel(stream.language_uuid)}
              </span>
            ) : null}
            {stream?.bit_rate ? (
              <span className="text-muted-foreground text-xs">{`${Math.round(Number(stream.bit_rate) / 1000)} kbps`}</span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
